// Proof of Work — client-side SHA-256 hash computation.
// Finds a nonce such that SHA-256(challenge + nonce) has the required leading zero bits.

const DIFFICULTY = 16; // Must match server-side PowDifficulty
const BATCH_SIZE = 10000;

export interface PowResult {
  nonce: string;
  hash: string;
  attempts: number;
  durationMs: number;
}

// Solve the PoW challenge. Returns the nonce that produces a valid hash.
// Runs in batches to avoid blocking the UI thread.
export async function solveProofOfWork(challenge: string): Promise<PowResult> {
  const start = performance.now();
  const encoder = new TextEncoder();
  let attempts = 0;

  while (true) {
    const promises: Promise<{ n: string; hash: ArrayBuffer }>[] = [];
    for (let i = 0; i < BATCH_SIZE; i++) {
      const candidate = String(attempts + i);
      const data = encoder.encode(challenge + candidate);
      promises.push(
        crypto.subtle.digest('SHA-256', data).then((hash) => ({ n: candidate, hash }))
      );
    }

    const results = await Promise.all(promises);

    for (const { n, hash } of results) {
      if (hasLeadingZeroBits(new Uint8Array(hash), DIFFICULTY)) {
        const elapsed = performance.now() - start;
        return {
          nonce: n,
          hash: arrayToHex(new Uint8Array(hash)),
          attempts: attempts + parseInt(n) + 1,
          durationMs: Math.round(elapsed),
        };
      }
    }

    attempts += BATCH_SIZE;

    // Yield to the event loop to keep UI responsive
    await new Promise((r) => setTimeout(r, 0));
  }
}

function hasLeadingZeroBits(data: Uint8Array, n: number): boolean {
  const fullBytes = Math.floor(n / 8);
  const remainBits = n % 8;

  for (let i = 0; i < fullBytes; i++) {
    if (data[i] !== 0) return false;
  }

  if (remainBits > 0) {
    const mask = 0xff << (8 - remainBits);
    if ((data[fullBytes] & mask) !== 0) return false;
  }

  return true;
}

function arrayToHex(arr: Uint8Array): string {
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
